// @/components/reusable/info-popover.jsx
// V2 Info Popover — thin wrapper around unified Popover system.
// Trigger modes: "click" (default) or "hover"

import React from "react";
import clsx from "clsx";
import { AnimatePresence } from "motion/react";
import InfoIcon from "@/assets/icons/InfoIcon";
import { Popover, PopoverButton, PopoverPanel, useHoverPopover } from './popover';

// Width mapping
const widthClasses = {
  sm: "w-48!",
  md: "w-64!",
  lg: "w-80!",
  xl: "w-96!",
};

/**
 * InfoPopover - V2 info popover with spring animation
 *
 * @param {string} content - HTML content to display
 * @param {string} trigger - Trigger mode: "click" | "hover" (default: "click")
 * @param {string} className - Additional classes for the button
 * @param {string} panelClassName - Additional classes for the panel
 * @param {string} anchor - Anchor position
 * @param {number} gap - Gap between button and panel (default: 8)
 * @param {string} width - Width class: "sm" | "md" | "lg" (default) | "xl"
 * @param {Function} onOpen - Callback when popover opens
 */
const InfoPopover = ({
  content,
  trigger = "click",
  className,
  panelClassName,
  anchor = "bottom",
  gap = 8,
  width = "lg",
  onOpen,
}) => {
  if (!content) return null;

  if (trigger === "hover") {
    return (
      <HoverInfoPopover
        content={content}
        className={className}
        panelClassName={panelClassName}
        anchor={anchor}
        gap={gap}
        width={width}
        onOpen={onOpen}
      />
    );
  }

  // Click mode — uses standard PopoverPanel
  return (
    <Popover className="relative inline-flex">
      <PopoverButton
        variant="text"
        className={clsx(
          "h-auto cursor-pointer transition-colors duration-150",
          "hover:text-primary focus:outline-hidden",
          className
        )}
      >

        <InfoIcon className="size-3" aria-hidden="true" />
        <span className="sr-only">More information</span>
      </PopoverButton>

      <PopoverPanel
        anchor={{ to: anchor, gap }}
        className={clsx(widthClasses[width] || widthClasses.lg, panelClassName)}
      >
        <div
          className="text-xs text-black font-medium font-fono tracking-tighter [&_p]:text-xs [&_p]:font-medium [&_p]:mb-2 [&_p:last-child]:mb-0 [&_b]:font-fono [&_b]:font-semibold [&_strong]:font-fono [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </PopoverPanel>
    </Popover>
  );
};

/**
 * HoverInfoPopover — hover-triggered variant using useHoverPopover hook
 */
function HoverInfoPopover({ content, className, panelClassName, anchor, gap, width, onOpen }) {
  const hover = useHoverPopover();

  // Fire onOpen callback
  React.useEffect(() => {
    if (hover.isOpen) onOpen?.();
  }, [hover.isOpen, onOpen]);

  return (
    <Popover className="relative inline-flex">
      <PopoverButton
        as="button"
        className={clsx(
          "cursor-help transition-colors duration-150",
          "hover:text-primary focus:outline-hidden",
          className
        )}
        {...hover.handlers}
      >
        <InfoIcon className="size-3" aria-hidden="true" />
        <span className="sr-only">More information</span>
      </PopoverButton>

      <AnimatePresence>
        {hover.isOpen && (
          <PopoverPanel
            hover
            anchor={{ to: anchor, gap }}
            className={clsx(widthClasses[width] || widthClasses.lg, panelClassName)}
            {...hover.handlers}
          >
            <div
              className="text-xs text-black font-medium font-fono tracking-tighter [&_p]:text-xs [&_p]:font-medium [&_p]:mb-2 [&_p:last-child]:mb-0 [&_b]:font-fono [&_b]:font-semibold [&_strong]:font-fono [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </PopoverPanel>
        )}
      </AnimatePresence>
    </Popover>
  );
}

export { InfoPopover };
export default InfoPopover;
